// state/appendice-rule.ts — parse a principle's `regola` field into scannable
// claim → verdetto rows for the Appendice "All'esame · le trappole" section
// (redesign spec §5.6).
//
// Real-data shape (assets/kb-data/principles-data.json, 331 principles):
//   • ~83% of `regola` values are a sequence of "claim → FALSO/VERO" statements,
//     often with a parenthetical reason and sometimes trailing definitional prose:
//     "Se la domanda dice che la strada non comprende marciapiedi → FALSO
//      (la strada li comprende). Se dice ... → FALSO. La BANCHINA è parte della
//      strada ma fuori dalla carreggiata, ..."
//   • ~17% are plain prose with no verdicts.
//
// We tokenize on the verdict arrow so a claim that contains commas/periods is
// kept whole, attach the trailing "(reason)" to its row, and emit any leftover
// non-verdict text as a prose row.

export type RuleRow =
  | { kind: 'verdict'; claim: string; verdict: 'VERO' | 'FALSO'; note?: string }
  | { kind: 'prose'; text: string };

// Connector prefixes to strip from the front of a claim for a clean row label.
const CLAIM_PREFIXES = [
  /^se la domanda dice che\s+/i,
  /^se la domanda dice\s+/i,
  /^se la domanda\s+/i,
  /^se dice che\s+/i,
  /^se dice\s+/i,
  /^la domanda dice che\s+/i,
  /^se\s+/i,
];

// Split a captured chunk into [leading prose sentences..., final claim]. The
// chunk preceding a "→ VERDETTO" may carry full context/definitional sentences
// before the actual claim (e.g. "In generale vale X. Se dice Y → FALSO" or a
// prose sentence sandwiched between two verdicts). We keep the LAST sentence as
// the claim and surface the earlier sentences as prose so no context is lost.
function splitLeadingProse(rawChunk: string): { lead: string | null; claim: string } {
  const norm = rawChunk.replace(/^[\s.;:,)]+/, '').trim();
  // Sentence boundary: a period followed by whitespace then a capital/quote/digit.
  const parts = norm.split(/(?<=\.)\s+(?=[A-ZÀ-Ù0-9"'«“])/);
  if (parts.length <= 1) return { lead: null, claim: norm };
  const claim = parts[parts.length - 1].trim();
  const lead = parts.slice(0, -1).join(' ').trim();
  return { lead: lead || null, claim };
}

function cleanClaim(raw: string): string {
  let c = raw
    // drop leading sentence-boundary punctuation/space left over from the
    // previous statement (". ", ") ", "; ", etc.)
    .replace(/^[\s.;:,)]+/, '')
    .replace(/\s+/g, ' ')
    .trim();
  for (const re of CLAIM_PREFIXES) {
    const next = c.replace(re, '');
    if (next !== c) { c = next.trim(); break; }
  }
  if (c) c = c.charAt(0).toUpperCase() + c.slice(1);
  return c;
}

/**
 * Parse a `regola` string into ordered rows. Returns a single prose row when
 * the text has no verdicts. Never throws; empty/whitespace input → [].
 */
export function parseRuleRows(rule: string | null | undefined): RuleRow[] {
  const norm = (rule ?? '').replace(/->/g, '→').trim();
  if (!norm) return [];
  if (!norm.includes('→')) return [{ kind: 'prose', text: norm }];

  const rows: RuleRow[] = [];
  const re = /(.+?)→\s*(VERO|FALSO)\s*(\(([^)]*)\))?/gis;
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(norm)) !== null) {
    const { lead, claim: rawClaim } = splitLeadingProse(m[1]);
    if (lead) rows.push({ kind: 'prose', text: lead });
    const claim = cleanClaim(rawClaim);
    const verdict = m[2].toUpperCase() as 'VERO' | 'FALSO';
    const note = (m[4] ?? '').trim() || undefined;
    if (claim) rows.push({ kind: 'verdict', claim, verdict, note });
    else if (rawClaim.trim()) rows.push({ kind: 'prose', text: rawClaim.trim() });
    lastIndex = re.lastIndex;
  }

  const tail = norm.slice(lastIndex).replace(/^[\s.;:,)]+/, '').trim();
  if (tail) rows.push({ kind: 'prose', text: tail });

  return rows;
}

/** True when the rule has at least one parseable verdict row (drives the section header). */
export function hasVerdictRows(rule: string | null | undefined): boolean {
  return parseRuleRows(rule).some((r) => r.kind === 'verdict');
}
